import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import * as util from '../../../shared/services/util.service';
import { Brand, IDigitalChecklist, IDigitalChecklistRequest, SortOrder } from '@signal/asp-data-commons';
import { Store } from '@ngrx/store';
import { DeliveryChecklistState } from '../../../store/delivery-checklist/delivery-checklist.reducer';
import * as fromActions from '../../../store/delivery-checklist/delivery-checklist.actions';
import * as fromSelector from '../../../store/delivery-checklist/delivery-checklist.selectors';
import { Observable } from 'rxjs';
import _ from 'lodash';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { selectChecklistForm, selectVehicleForm } from '../../../store/delivery-checklist/delivery-checklist.selectors';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-digi-checklist',
  templateUrl: './digi-checklist.component.html',
  styleUrls: ['./digi-checklist.component.scss']
})
export class DigiChecklistComponent implements OnInit, OnDestroy {
  @Input() isUpdate: boolean;
  @Output() formEdited: EventEmitter<boolean> = new EventEmitter();
  @Output() doVinsMatch: EventEmitter<boolean> = new EventEmitter();
  
  onlineChecklist$: Observable<IDigitalChecklist[]>;
  allChecklistLoading$: Observable<boolean>;
  formattedOnlineChecklist;
  allOnlineCategories;
  step: UntypedFormGroup;
  selectedCategoryTracker = {};
  private  readonly subs: SubscriptionList = {};
  private lastValue: any;
  checklistForm$: Observable<IDigitalChecklistRequest[]>;
  isLexus = util.environment.brand === Brand.LEXUS;
  actualVin='';
  enteredVin='';
  isVINCorrect=false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly checklistStore: Store<DeliveryChecklistState>,
    private readonly translate: TranslateService) {
  }

  ngOnInit(): void {
    this.onlineChecklist$ = this.checklistStore.select(fromSelector.selectOnlineChecklist);
    this.allChecklistLoading$ = this.checklistStore.select(fromSelector.selectAllChecklistLoading);
    this.subs.onlineChecklist = this.onlineChecklist$.subscribe(list => {
      this.formattedOnlineChecklist = this.getSortedChecklist(list);
      this.allOnlineCategories = Object.keys(this.formattedOnlineChecklist)
      this.allOnlineCategories.forEach(cat => { 
        this.selectedCategoryTracker[cat] = { total: this.formattedOnlineChecklist[cat].length, selected: 0, selectionText: "" } });
      if (list.length && !this.isUpdate) {
        this.ready(this.allOnlineCategories, this.formattedOnlineChecklist);
        this.updateChecklistToState();
      }
      this.checklistForm$ = this.checklistStore.select(selectChecklistForm);
      this.subs.checklistFromState = this.checklistForm$.subscribe(data => {
        if (data && this.isUpdate && this.allOnlineCategories.length) {
          if (_.isEqual(data, this.lastValue)) {
            return;
          }
          this.lastValue = data;
          this.ready(this.allOnlineCategories, this.formattedOnlineChecklist);
          if(data.length) {
          this.setFormValuesToSelectedChecklist(data);
          }
        }
      });
    });
    this.subs.selectVehicleForm = this.checklistStore.select(selectVehicleForm).subscribe(vehicle => {
      if (vehicle && vehicle?.vin) {
        this.actualVin = vehicle.vin;
      }
    });
  }

  private findSelectedChecklist(selectedChecklist: IDigitalChecklistRequest[], item) {
    return selectedChecklist.find(c => c.checklistId === item.checklistId);
  }

  private setChildChecklist(group: UntypedFormGroup, childArray: UntypedFormArray, selectedChildlist) {
    const item = group.value;
    childArray.controls.forEach(child => {
      if (selectedChildlist.includes(child.value.id) && !child.value.isChecked) {
        child.get('isChecked').patchValue(true);
        group.get('childSelected').patchValue(item.childSelected + 1);
      }
      else if (child.value.isChecked) {
        child.get('isChecked').patchValue(false);
        group.get('childSelected').patchValue(item.childSelected - 1);
      }
    });
  }

  private setSelectedValueToForm(cat, group, checklist) {
    const item = group.value;
    const checklistData = checklist.details;
    const selectedCount = this.selectedCategoryTracker[cat].selected;
    this.selectedCategoryTracker[cat].selected = item.isChecked ? selectedCount : selectedCount + 1;
    group.get('isChecked').patchValue(true);
    if (checklistData.childChecklist && checklistData?.childChecklist?.length) {
      const childArray = group.controls['childChecklist'] as UntypedFormArray;
      const selectedChildlist = checklistData?.childChecklist.map(x => x.id);
      this.setChildChecklist(group, childArray, selectedChildlist);
    }
  }

  setFormValuesToSelectedChecklist(checklistRequest: IDigitalChecklistRequest[]) {
    this.allOnlineCategories.forEach(i => {
      const catArray = this.step.controls[i] as UntypedFormArray;
      catArray.controls.forEach(item => {
        const selectedChecklist = this.findSelectedChecklist(checklistRequest, item.value);
        if (selectedChecklist) {
            this.setSelectedValueToForm(i, item, selectedChecklist);
          item.get('isExpanded').patchValue(item.value.isChecked &&  item.value?.childChecklist?.length ? true : false);
        }
      })

    })
  }

  getSortedChecklist(checklist: IDigitalChecklist[]) {
    let checklistItems = [...checklist];
    checklistItems = _.orderBy(checklistItems, ['seq'], [SortOrder.ASC]);
  
    return _.groupBy(checklistItems, "category");
  }

  updateChecklistToState() {
    const selectedChecklist = this.getSelectedChecklist();
    if(_.isEqual(selectedChecklist, this.lastValue)) {
      return;
    }
    this.lastValue = selectedChecklist.data;
    this.checklistStore.dispatch(new fromActions.CreateChecklist(selectedChecklist));
  }

  ready(cat, list) {
    const obj = {};
    cat.forEach(i => {
      const temp = this.setSelected(i, list[i]);
      obj[i] = this.formBuilder.array(temp);
    })
    this.step = this.formBuilder.group(obj);
  }

  createData(arr) {
    const temp = arr.map(item => {
      return {
        checklistId: item.checklistId,
        details: item
      }
    })
    return this.deleteExtraKeys(_.cloneDeep({ data: temp }), [ "childSelected", "isExpanded"]);
  }

  deleteExtraKeys(obj, parentKeyList) {
    obj.data.forEach(item => {
      parentKeyList.forEach(key => {
        delete item.details[key];
      })
      if (item?.details?.childChecklist?.length) {
        item.details.childChecklist.forEach((child, i) => {
          if (!child.isChecked) {
            delete item.details.childChecklist[i];
          }
        })
      }
    })
    return obj;
  }

  setSelected(cat, list) {
    return list.map(i => {
      const childChecklistArr = this.formBuilder.array([]);
      const obj = _.cloneDeep(i)
        obj.isChecked = false;
        obj.childSelected = 0;
        if (obj.isChildChecklistEnabled && obj.childChecklist && obj.childChecklist.length > 0) {
          const childSelected = 0;
          obj.childChecklist.forEach(j => {
            if (j.online) {
              j.isChecked = false;
              this.selectedCategoryTracker[cat].selected = childSelected > 0 ? this.selectedCategoryTracker[cat].selected + 1 : 
              this.selectedCategoryTracker[cat].selected;
              childChecklistArr.push(this.formBuilder.group(j));
            }
          })
      }
      delete obj.childChecklist;
      obj.isExpanded =  (childChecklistArr.value?.length > 0) || false ;
      return this.formBuilder.group({ ...obj, childChecklist: childChecklistArr });
    })
  }

  handleKeyEvent(event: KeyboardEvent) {
    const allowedKeys = ['ArrowDown', 'ArrowUp', 'Tab'];
    /* return if the pressed key is not specified above */
    if (!allowedKeys.includes(event.code)) return;
    event.preventDefault();
    /* get all expansion panels */
    const allPanels = document.querySelectorAll('mat-expansion-panel');
    let parentPanel: Element | null = null;
    /* get all mat checkboxes */
    const elements = Array.from(document.querySelectorAll('mat-checkbox input')) as any;
    /* get indices of all the mat checkboxes for navigation */
    const indices = elements.map(e => e.id);
    /* get current element */
    const element = event.target as HTMLElement;
    const id = element.id;
    /* get index of the current element */
    const index = indices.indexOf(id);
    let focusedElement = null;
    /* check the key type and navigate if the next element is available */
    if ((event.key === 'ArrowDown' || event.key === 'Tab') && index < elements.length - 1) {
      (elements[index + 1] as HTMLElement).focus();
      focusedElement = elements[index + 1];
    } else if (event.key === 'ArrowUp' && index > 0) {
      (elements[index - 1] as HTMLElement).focus();
      focusedElement = elements[index - 1];
    }
    /* check if the next element to be focused is inside the mat expansion panel */
    allPanels.forEach(panel => {
      if (panel.contains(focusedElement as HTMLElement)) {
        parentPanel = panel;
      }
    });
    /* if the next element is inside an expansion panel, open it to get focused */
    if (parentPanel) {
      const header = parentPanel.querySelector('mat-expansion-panel-header') as HTMLElement;
      if (header && !parentPanel.classList.contains('mat-expanded')) {
        header.click();
      }
    }
  }

  checkEventAndReturnData(event: any) {
    const data = { id: '', checked: false }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
      const checkbox = event.target as HTMLInputElement;
      /* get an id of the current element */
      data['id'] = checkbox.id.replace("-input", "");
      data['checked'] = !checkbox.checked;
    }
    else {
      data['id'] = event.source.id;
      data['checked'] = event.checked;
    }
    return data;
  }

  onParentCheckboxChange(e, cat, index) {
    const group = this.categoryFormArray(cat, index);
    const item = group.value;
    const { id, checked } = this.checkEventAndReturnData(e);
    if (id === item.checklistId) {
      item.isChecked = checked;
      item.isExpanded = item.isChecked && item.childChecklist.length > 0 ? true : item.isExpanded;
      if (!checked) {
        this.selectedCategoryTracker[cat].selected = this.selectedCategoryTracker[cat].selected - 1;
      } else {
        this.selectedCategoryTracker[cat].selected = this.selectedCategoryTracker[cat].selected + 1;
      }
      item.childChecklist.forEach(c => {
        c.isChecked = checked;
      })
      if (checked) {
        item.childSelected = item.childChecklist.length ? item.childChecklist.length : 0;
      } else {
        item.childSelected = 0;
      }
    }
    group.patchValue(item);
    this.updateChecklistToState();
    this.formEdited.emit(false);
  }

  categoryFormArray(cat: string, index) {
    return (this.step.controls[cat] as UntypedFormArray).controls[index] as UntypedFormGroup;
  }

  getSelectedChecklist() {
    const data = _.cloneDeep(this.allOnlineCategories.map(cat => this.step.controls[cat].value).flat());
    const filtered = data.filter(x => x.isChecked);
    return this.createData(filtered);
  }

  onChildCheckboxChange(e, cat, checklistId, childId, index, childIndex) {
    const group = this.categoryFormArray(cat, index);
    const item = group.value;
    if (item.checklistId === checklistId) {
      const c = item.childChecklist[childIndex];
      const { id, checked } = this.checkEventAndReturnData(e);
      if (id === childId) {
        c.isChecked = checked;
        if (checked) {
          this.selectedCategoryTracker[cat].selected = item.childSelected === 0 ?
            this.selectedCategoryTracker[cat].selected + 1 : this.selectedCategoryTracker[cat].selected;
          item.childSelected = item.childSelected + 1;
        }
        else {
          item.childSelected = item.childSelected - 1;
          if (item.childSelected === 0) {
            this.selectedCategoryTracker[cat].selected = this.selectedCategoryTracker[cat].selected - 1;
          }
        }
        item.isChecked = this.checkAllChild(item.childChecklist);
      }
      item.childChecklist[childIndex] = c;
    }
    group.patchValue(item);
    this.updateChecklistToState();
    this.formEdited.emit(false);
    /* patchValue removes the focus from the last selected child element, 
    bring the focus back to it if its not a click event */
    if (e?.srcElement) {
      setTimeout(() => {
        const current = e.srcElement as HTMLElement;
        const updated = document.getElementById(current.id);
        updated?.focus();
      });
    }
  }

  checkAllChild(list) {
    return list.some(i => i.isChecked);
  }

  getControls(category: string) {
    return (this.step.get(category) as UntypedFormArray).controls;
  }

  isValidForm() {
    const data = _.cloneDeep(this.allOnlineCategories.map(cat => this.step?.controls[cat].value).flat());
    return data.every((item) => item?.isMandatory ? (item?.isChecked && (item?.childChecklist && item?.childChecklist?.length ? item.childChecklist.every(child => child.isMandatory ? child.isChecked : true) : true)) : true)
  }

  getChecklistDescrMsg() {
    return this.translate.instant('deliveryChecklist.checklistDescMsg', {value: this.isLexus ? Brand.LEXUS : Brand.TOYOTA});
       
  }

  ngOnDestroy(): void {
    util.unsubscribeSubscriptions(this.subs);
  }

  validateVIN(event: Event) {
    const element = event.target as HTMLInputElement;
    this.enteredVin= element.value;
    if (this.enteredVin.length === 6 && this.enteredVin.toLocaleLowerCase() === this.actualVin.toLocaleLowerCase().substring(11)) {
      this.isVINCorrect = true;
      this.doVinsMatch.emit(true);
    }
    else {
      this.isVINCorrect = false;
      this.doVinsMatch.emit(false);
    }
  }
}


